import { useLocale } from '@lessonup/client-integration';
import { useTextEditorState, useUpdatePin } from '@lessonup/pin-renderer';
import { newTextPinComponent, PinComponent, Size, TeacherPin } from '@lessonup/pins-shared';
import {
  color,
  colorSets,
  ListItem,
  SpaceBetween,
  spacing,
  spacingScaled,
  styled,
  TextElement,
} from '@lessonup/ui-components';
import { JSONContent } from '@tiptap/core';
import React, { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ContextSidebarGroup } from '../../../../../../components/ContextSidebar/ContextSidebarGroup';
import { aiToolboxRoute } from '../../../../../../utils/bridge/teacherRoutes';
import { useFeatureFlags } from '../../../../../../utils/featureFlags/useFeatureFlags';
import { AiToolbox } from '../../../../../AiToolbox/AiToolbox';
import {
  AiToolsStandardGeneratorType,
  aiToolsTypeToEventType,
  editorGenerators,
  isStandardGenerator,
} from '../../../../../AiToolbox/AiToolbox.types';
import { i18nextNamespaceAiTools } from '../../../../../AiToolbox/AiToolbox.utils';
import { AiToolboxContext } from '../../../../../AiToolbox/context/AiToolboxContext';
import { i18nextNamespace } from '../../../../Editor.utils';
import { useEditorContext } from '../../../../context/EditorContext/EditorContext';
import { useActivePin, useActivePinId } from '../../../../context/EditorContext/hooks/useEditorState';
import { createAddPinComponent } from '../../../../utils/pin/pin.utils';
import { teacherTextEditorStore } from '../../../../utils/yjs/EditorYjs/teacherTextEditorStore';
import { useEditorContextSidebarActions } from '../../hooks/useEditorContextSidebar';

interface AiToolsPaneProps {
  lessonId?: string;
  setActiveGenerator: (generator: AiToolsStandardGeneratorType) => void;
  activeGeneratorType: AiToolsStandardGeneratorType | null;
  selectedPinComponent: PinComponent | null;
  hasSelectedMultiplePinComponents: boolean;
}

export const AiToolsPane = ({
  lessonId,
  setActiveGenerator,
  activeGeneratorType,
  selectedPinComponent,
  hasSelectedMultiplePinComponents,
}: AiToolsPaneProps) => {
  const { t } = useTranslation(i18nextNamespaceAiTools);
  const { editor, textHandler: textEditorHandler } = useTextEditorState(teacherTextEditorStore);
  const { isFeatureEnabled } = useFeatureFlags();
  const dispatch = useUpdatePin();
  const pin = useActivePin();
  const afterPinId = useActivePinId();
  const { setActivePinId, setModeSelecting } = useEditorContext();
  const locale = useLocale();
  const [initialBody, setInitialBody] = useState<JSONContent | undefined>(undefined);
  const addPinComponent = createAddPinComponent(dispatch, setModeSelecting, pin);

  const isTextComponent = selectedPinComponent?.type === 'TEXT';

  const handleAddPins = useCallback(
    async (pins: TeacherPin[]) => {
      dispatch({
        type: 'addPins',
        afterPinId: afterPinId ?? undefined,
        pins,
        onComplete: (pinIds: string[]) => {
          setActivePinId(pinIds[0]);
        },
      });
    },
    [dispatch, afterPinId, setActivePinId]
  );

  const handleAddPinComponent = useCallback(
    (content: JSONContent, size?: Size) => {
      addPinComponent({
        newComponent: (position) => newTextPinComponent({ position, content, size }),
        aiToolType: activeGeneratorType ? aiToolsTypeToEventType[activeGeneratorType] : undefined,
      });
    },
    [addPinComponent, activeGeneratorType]
  );

  const handleReplaceSelection = useCallback(
    (section: JSONContent) => {
      textEditorHandler.selection.replace(section);
    },
    [textEditorHandler]
  );

  const getActiveSelectionOrAllText = useCallback((): JSONContent | undefined => {
    if (!editor) {
      return undefined;
    }

    if (editor.state.selection.empty) {
      return editor.getJSON();
    }

    const { from, to } = editor.state.selection;
    const selectedContent = editor.state.doc.slice(from, to, true);
    const { content } = selectedContent.toJSON();

    return {
      type: 'doc',
      content,
    };
  }, [editor]);

  // This should not work, the sidebar rerenders when the selection changes :/
  const hasActiveValidSelection =
    !!selectedPinComponent && isTextComponent && !hasSelectedMultiplePinComponents && !!getActiveSelectionOrAllText();

  const filteredGenerators = editorGenerators.filter(isStandardGenerator).filter((generator) => {
    if (generator.featureFlag) return isFeatureEnabled(generator.featureFlag);
    return true;
  });

  const handleUpdateBody = () => {
    const hasActiveValidSelection =
      !!selectedPinComponent && isTextComponent && !hasSelectedMultiplePinComponents && !!getActiveSelectionOrAllText();

    if (hasActiveValidSelection) {
      setInitialBody(getActiveSelectionOrAllText());
    }
  };

  useEditorContextSidebarActions({
    aiToolsUpdateBody: () => {
      handleUpdateBody();
    },
    aiToolsGenerateImage: () => {
      setActiveGenerator('image');
    },
  });

  if (activeGeneratorType) {
    const activeGenerator = filteredGenerators.find((generator) => generator.type === activeGeneratorType);

    if (!activeGenerator) {
      return null;
    }

    return (
      <AiToolboxContext.Provider
        value={{
          initialBody,
          addPins: handleAddPins,
          addPinComponent: handleAddPinComponent,
          lessonId,
          replaceSelection: handleReplaceSelection,
          showInline: true,
          hasActiveValidSelection,
        }}
      >
        <ContextSidebarGroup>
          <AiToolbox slug={activeGenerator.slug} />
        </ContextSidebarGroup>
      </AiToolboxContext.Provider>
    );
  }

  return (
    <ContextSidebarGroup>
      <SpaceBetween direction="y" spacing={spacing.size8}>
        {filteredGenerators.map((generator) => (
          <StyledListItem
            key={generator.slug}
            text={t(generator.labelKey)}
            secondLine={t(generator.descriptionKey)}
            icon={<generator.icon />}
            onClick={() => {
              setActiveGenerator(generator.type);
              setInitialBody(hasActiveValidSelection ? getActiveSelectionOrAllText() : undefined);
            }}
            disabled={!!hasActiveValidSelection && generator.type === 'lessonRecap'}
            roundedCorners
            indentation
          />
        ))}
      </SpaceBetween>
      <MoreInfo>
        <StyledTextElement textStyle="caption">
          <Trans
            i18nKey="contextSidebar.panes.aiTools.description"
            ns={i18nextNamespace}
            components={[
              <a
                key="0"
                href={aiToolboxRoute.href({ language: locale.language })}
                target="_blank"
                rel="noopener noreferrer"
              >
                {'content'}
              </a>,
            ]}
          />
        </StyledTextElement>
      </MoreInfo>
    </ContextSidebarGroup>
  );
};

const StyledListItem = styled(ListItem)`
  border: ${spacingScaled(spacing.size2, 'medium')} solid ${colorSets.neutral.stroke};
`;

const MoreInfo = styled.div`
  margin-top: ${spacing.size16};
`;

const StyledTextElement = styled(TextElement)`
  color: ${color.additional.disabled.text};
`;
